import type { ReactNode } from 'react'
import { Bounce, toast } from 'react-toastify'
import { ToastIcon, ToastOptions } from 'react-toastify/dist/types'
import { AxiosError } from 'axios'

export type ToastType = 'info' | 'success' | 'warning' | 'error'

export const TOAST_IDS = {
	UPLOAD: 'upload-notification',
} as const

interface ToastCallerProps extends ToastOptions {
	errorMessageCode?: string
	message?: ReactNode | string
	icon?: ToastIcon
	type?: ToastType
	objectName?: string | null
	withClosePrevious?: boolean
	update?: boolean
	toastId?: string | number
}

export const HANDLED_SERVER_ERROR_CODES = [
	'EXTENSION_NOT_ALLOWED',
	'FILE_CONTENT_NOT_MATCH_EXTENSION',
	'IMAGE_INVALID_ASPECT_RATIO',
	'IMAGE_INVALID_RESOLUTION',
]

const toastCaller = ({
	errorMessageCode,
	message,
	icon,
	type = 'error',
	objectName,
	withClosePrevious,
	update,
	toastId,
	...options
}: ToastCallerProps) => {
	if (withClosePrevious) {
		toast.dismiss()
	}

	let errorMessageText: ReactNode | string =
		message || errorMessageCode || 'Error'

	if (errorMessageCode) {
		if (errors?.[errorMessageCode]) {
			errorMessageText = errors?.[errorMessageCode](objectName)
		} else {
			const match = errorMessageCode.match(/^(\w+)_SAME_NAME_EXISTS$/)
			if (match) {
				const [, name] = match
				errorMessageText = errors.SAME_NAME_EXISTS(objectName, name)
			} else if (typeof errors[errorMessageCode] === 'function') {
				errorMessageText = errors[errorMessageCode](objectName)
			}
		}
	}

	if (update && toastId && toast.isActive(toastId)) {
		toast.update(toastId, {
			render: errorMessageText,
			type,
			autoClose: options.autoClose ?? 3000,
			...options,
		})
		return toastId
	}

	return toast[type](errorMessageText, {
		position: 'top-right',
		icon,
		autoClose: options.autoClose ?? 3000,
		hideProgressBar: false,
		closeOnClick: false,
		draggable: true,
		transition: Bounce,
		className: 'bg-background-notification text-white',
		...options,
		...(toastId !== undefined ? { toastId } : {}),
	})
}

export default toastCaller

export const errors: Record<
	string,
	(objectName?: string, name?: string) => string
> = {
	//common
	UNAUTHORIZED: () => 'You are not authorized. Please sign in again.',
	SAME_NAME_EXISTS: (objectName, name) =>
		`${
			objectName || `The ${(name || 'object').toLowerCase()}`
		} already exists!`,
	ACCESS_DENIED: () => 'No access rights',
	NAME_TOO_LONG: () => 'Name too long!',
	NAME_TOO_SHORT: () => 'Name too short!',
	NAME_REQUIRED: () => 'Name is required',
	NAME_ONLY_WHITESPACE: () => 'Name cannot be only whitespace',
	FOLDER_NAME_RESERVED: () => 'This folder name is reserved (Windows)',
	INVALID_NAME: () => 'Invalid name',
	NAME_INVALID_BOUNDARY: () => 'Name cannot start or end with spaces or dots',
	NAME_INVALID_CHARACTERS: () =>
		'Name contains invalid characters: \\ / : * ? " < > |',
	FILE_ID_REQUIRED: () => 'File ID is required',
	FOLDER_ID_REQUIRED: () => 'Folder ID is required',
	PARENT_ID_REQUIRED: () => 'Parent ID is required',
	INVALID_PAYLOAD: () => 'Invalid request data',

	// User profile
	EMAIL_REQUIRED: () => 'Email is required',
	EMAIL_INVALID: () => 'Email format is invalid',
	FIRST_NAME_INVALID: () =>
		'First name can only contain letters, spaces, apostrophes, and hyphens',
	LAST_NAME_INVALID: () =>
		'Last name can only contain letters, spaces, apostrophes, and hyphens',
	FIRST_NAME_REQUIRED: () => 'First name is required',
	FIRST_NAME_TOO_SHORT: () => 'First name is too short',
	FIRST_NAME_TOO_LONG: () => 'First name is too long',
	LAST_NAME_REQUIRED: () => 'Last name is required',
	LAST_NAME_TOO_SHORT: () => 'Last name is too short',
	LAST_NAME_TOO_LONG: () => 'Last name is too long',
	PHONE_REQUIRED: () => 'Phone is required',
	PASSWORD_CHANGE_FAILED: () => 'Request to change password failed.',

	// organization
	ORGANIZATION_SLUG_ALREADY_EXISTS: () => 'This slug is already used',

	// permissions
	PERMISSION_SET_CONFIRMATION_REQUIRED: () =>
		'Set is applied to other objects.',

	// Notes
	TITLE_REQUIRED: () => 'Title is required',
	TITLE_TOO_LONG: () => 'Title is too long (max 40 characters)',
	BODY_REQUIRED: () => 'Content is required',
	BODY_TOO_LONG: () => 'Content is too long (max 200 characters)',
	DATE_INVALID: () => 'Date has invalid format',

	//inviting users
	USER_UNABLE_TO_CREATE: (objectName?: string) =>
		`${objectName}: User's inviting is not possible... Please try again later`,
	USER_ALREADY_EXISTS: (objectName?: string) =>
		`${objectName}: The user you are trying to invite already exists`,
	USER_ALREADY_IN_ORGANIZATION: (objectName?: string) =>
		`${objectName}: The user you are trying to invite already in organization`,
	UNABLE_TO_SEND_EMAIL: (objectName?: string) =>
		`${objectName}: Unable to send invite. Please try to resend it later`,
	USER_UNABLE_TO_INVITE_WRONG_DOMAIN: (objectName?: string) =>
		`${objectName}: Wrong domain`,
	ORGANIZATION_DOMAIN_NOT_ALLOWED: () =>
		"Personal email domains can't be used as an organization domain.",
	ORGANIZATION_INVALID_DOMAIN: () =>
		'Invalid domain. Please enter a valid organization domain.',

	//files
	VERSION_NOT_FOUND: () => 'Version not found',
	FILE_FORMAT_NOT_SUPPORTED: () => 'File format not supported',
	FILE_NOT_SUPPORTED_FORMAT: () => "File type doesn't supported for preview",
	EXTENSION_REQUIRED: () => 'File extension is required',
	INVALID_FILE_EXTENSION: () =>
		'Invalid file extension. Allowed: .txt, .csv, .pdf, .docx, .xlsx, .ppt',
	ARCHIVE_EXCEEDS_SIZE_LIMIT: () =>
		'The maximum size of all files must not exceed 4 GB',
	FILE_LOCKED: () => "This file's already locked!",
	FILE_UNABLE_TO_UNLOCK: () => 'File unable to unlock!',
	FORBIDDEN_FILE_TYPE: () => 'File type not allowed for security reasons',
	FILE_TOO_BIG: () =>
		'The file is too large and is not supported for previewing',
	PREVIEW_NOT_READY: () => 'Preview is being generated, please wait...',
	THUMBNAIL_NOT_READY: () => 'Thumbnail is being generated, please wait...',

	// Thumbnail errors
	FILE_THUMBNAIL_NOT_AVAILABLE: () => 'File was created without content',
	FILE_THUMBNAIL_NOT_READY: () =>
		'Thumbnail is being generated, please wait...',
	FILE_THUMBNAIL_UNSUPPORTED_FORMAT: () =>
		"File type doesn't supported for thumbnail",

	// Preview errors
	FILE_PREVIEW_NOT_AVAILABLE: () => 'File was created without content',
	FILE_PREVIEW_NOT_READY: () => 'Preview is being generated, please wait...',
	FILE_PREVIEW_UNSUPPORTED_FORMAT: () =>
		"File type doesn't supported for preview",
	FILE_UNABLE_TO_GENERATE_PREVIEW: () => 'File unable to generate preview',
	PREVIEW_GENERATION_TIMEOUT: () =>
		'Preview generation timeout. Please try again later.',

	//folders
	PARENT_FOLDER_NOT_FOUND: () => 'Folder does not exists',
	FOLDER_NOT_FOUND: () => 'This folder does not exists',

	//categories
	CATEGORY_HAS_FILES: () =>
		'Action impossible! A category has files associated with it',
	CATEGORY_DUPLICATE_FIELD_NAME: (objectName?: string) =>
		`This category already has such a field.`,

	// wiki
	WIKI_TOPIC_HAS_WIKI_PAGES: () =>
		"You can't delete it. This topic has wiki pages.",

	//boards
	TODO_CARD_INVALID_INDEX: () => 'Wrong position, try to move again',

	//limits
	STORAGE_QUOTA_EXCEEDED: () =>
		'The maximum storage capacity has been exceeded.',
	USERS_QUOTA_EXCEEDED: (objectName?: string) =>
		`${
			objectName ? objectName : ''
		} The maximum number of users has been exceeded`,
	MAXUSERS_LESSTHAN_CURRENT_USAGE: () =>
		'Users count is less, than current in organization. Please select bigger value for it.',

	// billing
	BILLING_PLAN_NOT_FOUND: () => 'This plan not found.',
	BILLING_CURRENCY_NOT_AVAILABLE_FOR_REGION: () =>
		'The selected currency is not available in your region. Please contact support for help with your subscription.',
	BILLING_PLAN_ALREADY_SUBSCRIBED: () =>
		'You are already subscribed to this plan. To change the users and storage, edit usage and quota from the billing page.',
	STRIPE_NO_ACTIVE_SUBSCRIPTION_FOUND: () =>
		"You don't have any active subscriptions to cancel it.",
	STRIPE_PRODUCT_DISABLED: () => 'This plan disabled. Please try again later.',
	BILLING_INVOICE_NOT_FOUND: () =>
		'Current plan not available. Please try to change plan.',
	CURRENT_PLAN_NOT_AVAILABLE: () =>
		'Current plan not available. Please try to change plan.',

	//signing
	ESIGN_INVALID_FIELDS: () => 'Missing fields',
	ESIGN_INVALID_FIELD_RECIPIENT: () => 'Invalid signer field',
	ESIGN_INVALID_STATUS_TRANSITION: () => 'Invalid eSign item action',
	EXTENSION_NOT_ALLOWED: () => 'This file extension is not allowed.',
	FILE_CONTENT_NOT_MATCH_EXTENSION: () =>
		'File content does not match its extension.',
	IMAGE_INVALID_ASPECT_RATIO: () => 'Image must have a 1:1 aspect ratio.',
	IMAGE_INVALID_RESOLUTION: () =>
		'Image resolution is invalid. Please choose another image.',
}

// export const errors: Record<string, (objectName?: string) => string> = {
// FOLDER_SAME_NAME_EXISTS: (objectName) =>
// `${objectName || 'The folder'} already exists!`,
// FILE_SAME_NAME_EXISTS: (objectName) =>
// `${objectName || 'The file'} already exists!`,
// CATEGORY_SAME_NAME_EXISTS: (objectName) =>
// `${objectName || 'The category'} already exists!`,
// }
